import { createClient } from '@supabase/supabase-js';
import { Platform } from 'react-native';
import * as Linking from 'expo-linking';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { SUPABASE_URL, SUPABASE_ANON_KEY } from './constants';

if (!SUPABASE_URL || !SUPABASE_ANON_KEY) {
  throw new Error('Missing Supabase environment variables');
}

export const supabase = createClient(SUPABASE_URL, SUPABASE_ANON_KEY, {
  auth: {
    // Use AsyncStorage for session persistence on mobile
    storage: Platform.OS !== 'web' ? AsyncStorage : undefined,
    persistSession: true,
    autoRefreshToken: true,
    detectSessionInUrl: true
  },
  // Disable realtime on mobile - it uses Node.js 'ws' package which isn't compatible with React Native
  // Realtime will still work on web where 'ws' is available
  realtime: Platform.OS === 'web' ? {
    params: {
      eventsPerSecond: 10
    }
  } : undefined,
  global: {
    headers: {
      'x-application-name': 'honeydone'
    }
  }
});

export const createTestUser = async (userData: {
  email: string;
  password: string;
  fullName: string;
  phoneNumber: string;
  lifecycle_status?: 'lead' | 'walkthrough' | 'first_visit' | 'active' | 'churned';
}) => {
  try {
    // Create user in Supabase
    const { data: authData, error: authError } = await supabase.auth.signUp({
      email: userData.email,
      password: userData.password,
    });

    if (authError) throw authError;
    if (!authData.user) throw new Error('No user data returned');

    // Create profile
    const { error: profileError } = await supabase
      .from('profiles')
      .insert({
        id: authData.user.id,
        full_name: userData.fullName,
        phone_number: userData.phoneNumber,
        lifecycle_status: userData.lifecycle_status || 'lead'
      });

    if (profileError) throw profileError;

    return authData.user;
  } catch (error) {
    console.error('Error creating test user:', error);
    throw error;
  }
};

export const resetPassword = async (email: string) => {
  // Get redirect URL based on platform
  let redirectUrl: string;
  if (Platform.OS === 'web' && typeof window !== 'undefined') {
    redirectUrl = `${window.location.origin}/reset-password`;
  } else {
    // For mobile, use the app scheme
    const scheme = 'honeydone';
    redirectUrl = `${scheme}://reset-password`;
  }

  const { error } = await supabase.auth.resetPasswordForEmail(email, {
    redirectTo: redirectUrl,
  });
  
  if (error) throw error;
};

// Helper function to check if a Supabase error is a connection error
export const isSupabaseConnectionError = (error: any) => {
  return error?.message?.includes('Failed to fetch') || 
         error?.message?.includes('NetworkError') ||
         error?.message?.includes('network request failed');
};

// Helper function to handle Supabase errors
export const handleSupabaseError = (error: any) => {
  if (isSupabaseConnectionError(error)) {
    return new Error('Unable to connect to the server. Please check your internet connection and try again.');
  }
  return error;
};